<template>
    <p v-if="tag === 'p'" v-html="description" class="html-content"></p>
    <article v-else-if="tag === 'article'" v-html="description" class="html-content"></article>
    <main v-else-if="tag === 'main'" v-html="description" class="html-content"></main>
    <div v-else-if="tag === 'div'" v-html="description" class="html-content"></div>
    <section v-else-if="tag === 'section'" v-html="description" class="html-content"></section>
    <pre v-else v-html="description" class="html-content"></pre>
</template>

<script lang="ts" setup>
const props = defineProps({
    tag: {
        type: String as PropType<"p" | "article" | "main" | "div" | "section" | "pre">,
        default: "article",
    },
    description: String,
});
</script>

<style scoped>
/* General Container */
.html-content {
    @apply leading-7 text-justify;
    color: #444;
}

/* Headings */
.html-content :deep(h1) {
    @apply text-4xl font-bold mt-6 mb-2 text-gray-900;
}

.html-content :deep(h2) {
    @apply text-3xl font-semibold mt-6 mb-2 text-gray-800;
}

.html-content :deep(h3) {
    @apply text-2xl font-semibold mt-6 mb-2 text-gray-700;
}

.html-content :deep(h4),
.html-content :deep(h5),
.html-content :deep(h6) {
    @apply text-xl font-medium mt-4 mb-1 text-gray-700;
}

/* Paragraphs */
.html-content :deep(p) {
    @apply mt-2 text-gray-600 indent-4;
}

/* Links */
.html-content :deep(a) {
    @apply text-amazon-500 underline hover:text-amazon-600;
}

/* Lists */
.html-content :deep(ul) {
    @apply list-disc mt-4 px-1 list-inside;
}
.html-content :deep(ol) {
    @apply list-decimal mt-4 px-1 list-inside;
}

.html-content :deep(ul li),
.html-content :deep(ol li) {
    @apply mt-2;
}

/* Blockquotes */
.html-content :deep(blockquote) {
    @apply border-l-4 pl-4 italic text-gray-700 bg-gray-100 border-amazon-500;
    margin: 1.5em 0;
}

/* Code */
.html-content :deep(pre) {
    @apply bg-gray-800 text-gray-200 p-4 rounded-md overflow-x-auto mb-2;
    font-size: 0.95em;
    line-height: 1.45;
}

.html-content :deep(code) {
    @apply bg-gray-200 text-pink-500 px-2 py-1 rounded-md;
    font-family: "Courier New", Courier, monospace;
}

/* Images */
.html-content :deep(img) {
    @apply w-full h-auto my-6 rounded-md;
}

/* Tables */
.html-content :deep(table) {
    @apply w-full border-collapse mb-6;
}

.html-content :deep(table th),
.html-content :deep(table td) {
    @apply border border-gray-200 p-3 text-left;
}

.html-content :deep(table th) {
    @apply bg-gray-100 font-semibold;
}

.html-content :deep(table tr:nth-child(even)) {
    @apply bg-gray-50;
}

/* Horizontal Rule */
.html-content :deep(hr) {
    @apply border-0 h-px bg-gray-200 my-4;
}

/* Emphasis */
.html-content :deep(strong) {
    @apply text-gray-900 font-bold;
}

.html-content :deep(em) {
    @apply italic text-amazon-500;
}

/* Custom Classes (Optional) */
.html-content :deep(.highlight) {
    @apply bg-yellow-200 px-1 py-0.5 rounded;
}

/* Media Queries for Responsiveness */
@media (max-width: 600px) {
    .html-content :deep() {
        @apply text-base leading-8;
    }

    .html-content :deep(h1) {
        @apply text-3xl;
    }

    .html-content :deep(h2) {
        @apply text-2xl;
    }

    .html-content :deep(h3) {
        @apply text-xl;
    }
}
</style>
